import { useEffect, useId, useRef, useState } from 'react';

/**
 * A CIRCLED "i" THAT REVEALS A SENTENCE — the card's dense variant.
 *
 * The open card's properties are a tight two-column list, one row per field,
 * with no room for a standing hint line under each control. So the help moves
 * into a toggletip next to the label: the field stays a single quiet row, and
 * the explanation is a tap or a Tab away for whoever wants it.
 *
 * WHY NOT `Info`. That one opens on HOVER and carries a title, which is right
 * for a chip a mouse rests on. This help is a full sentence that must reach a
 * touch screen and a keyboard equally, so it opens on CLICK and on keyboard
 * FOCUS instead, and reveals into a `role="status"` live region rather than
 * being wired with `aria-describedby` — a described-by on a dozen fields reads
 * the whole paragraph out on every field, which is worse than silence.
 *
 * THE POP HANGS FROM THE "i" ITSELF, NOT THE ROW IT SITS IN. Vinnie, 30 Sep
 * 2026, seeing it open at the far-left of a wide heading: *"The 'i' open card
 * should always be next to the 'i'… it should always be at the top of the 'i'
 * with a little arrow forming part of the card and should not look detached, or
 * buggy. This should be a design rule for all 'i' tips."* So the button and pop
 * are wrapped in a `position: relative` span that hugs just the "i": the pop
 * anchors to that wrapper — right under the dot, wherever the dot happens to sit
 * — and a caret on the card's top edge points back up at it, so the card reads
 * as attached rather than floating loose.
 */
export default function InfoTip({
  /** The field this explains, for the button's spoken label ("About Phase"). */
  label,
  /** The explanation itself. */
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLSpanElement>(null);
  /*
   * A click focuses the button before it clicks it, so a naive "focus opens"
   * would fight "click toggles" and the first click would open-then-close.
   * This records that a pointer started the interaction, so focus opens ONLY
   * when it came from the keyboard; the click handler owns the mouse path.
   */
  const fromPointer = useRef(false);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!btn.current?.contains(t) && !pop.current?.contains(t)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Close only the tip. This listener is on `document`, which Escape
        // reaches before a host card/modal's own `window` Escape handler — stop
        // it here so one press dismisses the tip, not the whole form around it
        // (which would throw away anything typed). A second press then backs out
        // of the form as usual.
        e.stopPropagation();
        setOpen(false);
        btn.current?.blur();
      }
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <span className="infotip-wrap">
      <button
        ref={btn}
        type="button"
        className="infotip"
        /* Out of the tab order — tabbing a card runs field to field, and the
           help dot is not a field. Still reachable by mouse/touch (click) and
           to a screen reader's element cursor via its aria-label. */
        tabIndex={-1}
        aria-label={`About ${label}`}
        aria-expanded={open}
        aria-controls={id}
        onPointerDown={() => {
          fromPointer.current = true;
        }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onFocus={() => {
          if (!fromPointer.current) setOpen(true);
          fromPointer.current = false;
        }}
        onBlur={() => setOpen(false)}
      >
        <span className="dot" aria-hidden="true">
          i
        </span>
      </button>
      <span
        ref={pop}
        className="infotip-pop"
        id={id}
        role="status"
        hidden={!open}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </span>
    </span>
  );
}
